// Signup page — same LoginWindow chrome as /signin so OAuth works as
// "sign up with Google/GitHub" (sdk.googleAuth / sdk.githubAuth on
// the Symbols server upserts the user, so first-time vs returning
// is handled server-side), and the email/password path uses
// SignupForm with the SDK register call.
//
// PERSONA AUDIT r1 (elena: 150% zoom; sam: keyboard only) — what this page now
// does beyond the form:
//   • the keyboard order IS the visual order: the form first, the OAuth column
//     after it (the white-label mixin used to come first in `extends`, which put
//     its `RequestAccessButtons` slot ahead of the form in the DOM);
//   • the right column speaks sign-up ("Another way to sign up", "Continue with
//     Google"), the form says where an existing account signs in, and the
//     privacy policy + terms are two links under the button;
//   • the password rule is printed under the field — the server's rule as a
//     live checklist when the SDK can read the sign-up policy, the rule's own
//     sentence otherwise;
//   • a finished sign-up STAYS on this page: "Check your email" names the
//     address, says whether the mail went out, and hands the person on with
//     one button — into the new account, signed in (`register` answers with
//     the account's session; R2-m10), or, when there is no session to open, to
//     sign-in with the address already in the field. It used to be a
//     bottom-centre toast on the NEXT page (it covered the Google button at
//     150% zoom, vanished in 3.5 s, named no address) and focus fell to <body>
//     on the page change. Focus now lands on the heading and the message is
//     read from an always-mounted live region;
//   • the page's own errors are told IN the form, not in a bottom-centre toast
//     (at 150% zoom the toast sat on the Sign up button): an empty submit or a
//     failed register in a line above the button, a refused password in a line
//     under the rule;
//   • a short screen scrolls and the card keeps its content (see the card's
//     `flexShrink` below).

import { stashOAuthNext } from '../../functions/postOAuthNext.js'
import { authPageTheme } from './theme.js'

// A visually hidden box that stays in the accessibility tree — the sr-only
// idiom of components/PageSrTitle.js, in tokens: never display / visibility /
// opacity, those take the text out of the one place it has to be. Spread into
// definitions at load; no handler reads it.
const SR_ONLY = {
  position: 'absolute',
  width: 'hairline',
  height: 'hairline',
  padding: '0',
  border: '0',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap'
}

// Seed the form from the invitation in `?token=`. An EMAILED invitation names
// its address: the field is set to it and locked (the server accepts only that
// address). A MAGIC LINK (INVITE-MAGIC-LINK-1) names none: the field stays
// empty and editable — any address may join through it. It used to lock the
// field for ANY token, which on a magic link left an empty field nobody could
// type into. The claims are read for the form only, never for access
// (functions/inviteToken.js); a malformed token seeds nothing.
//
// Runs on the LoginWindow (its onInit, below): LoginWindow declares its own
// state, and that is the scope SignupForm reads — a write to the PAGE state
// never reached the field (same reason as reset-password.js).
const _seedFromInviteToken = (el, s, token) => {
  if (typeof window === 'undefined') return
  if (!token) return
  const claims = el.call('inviteTokenClaims', token)
  if (claims && claims.email) {
    s.quietUpdate({ email: claims.email, hasToken: true, inviteEmailBound: true })
  } else if (claims) {
    s.quietUpdate({ hasToken: false, inviteEmailBound: false })
  }
}

// Name the org the invite joins (INVITE-JOIN-PAGE-
// PERSONALIZED-1) so signup can say "Join Acme" instead of the generic
// headline. Feature-detected: no preview method (older SDK/server) or a
// dead token just keeps the generic copy — never blocks the form.
const _seedInvitePreview = async (el, s, token) => {
  if (typeof window === 'undefined') return
  try {
    if (!token || typeof el.sdk?.getInvitationPreview !== 'function') return
    const res = await el.sdk.getInvitationPreview({ token })
    const p = res?.data || res || null
    // The ORGANIZATION's name heads the card — "Join <org>" for every invite
    // (Nika 2026-10-14: "instead of 'Join Default' we say 'Join orgname'"; a
    // workspace name such as an org's default "Default" says nothing to an
    // invitee). The workspace name is the fallback for an older preview that
    // carries no org name.
    const name = p?.orgName || p?.organizationName || p?.workspaceName || ''
    if (name && p?.status !== 'expired' && p?.status !== 'revoked') {
      s.update({ inviteTargetName: name, inviteInviterName: p?.inviterName || '' })
    }
  } catch (_) {
    /* personalization only — the generic headline stands */
  }
}

// TAKE the invitation token OUT of the address bar, once, on mount — the same
// way the accept page does (functions/inviteToken.js takeInviteToken: the boot
// step in workspace/index.html parks it on a hard load, an in-app navigation
// reads the address bar). It is a bearer credential; /signup used to keep
// `?token=` in the URL for the whole visit (D2 follow-up, live on the
// white-label landing 2026-10-11). The copy lives on the LoginWindow element
// (never reactive state, never storage) for the seed, the preview and the
// register call. Without takeInviteToken registered, the address bar is read
// as before.
//
// A RELOAD of /signup has no `?token=` left (it was taken on the first mount),
// but the accept page's `next` return path still carries it — so the invite
// context (locked email, "Join <org>") is rebuilt from there
// (functions/inviteToken.js inviteTokenFromNext).
const _takeSignupInvite = (el) => {
  if (typeof window === 'undefined') return null
  const canCall = typeof el.call === 'function'
  const taken = canCall ? el.call('takeInviteToken') : undefined
  if (taken) return taken
  const fromNext = canCall ? el.call('inviteTokenFromNext') : undefined
  if (fromNext) return fromNext
  if (taken !== undefined) return null
  return new URLSearchParams(window.location.search).get('token')
}

export const signup = {
  extends: 'Page',
  // On a white-label host the tab names the org ("<page> / <org>"). While the
  // brand is not there, the page answers NO title: the edge-stamped one stays
  // while it loads, and the shell's route wrapper supplies its own title once
  // the host turns out to have no brand (shell functions/authWrapperTitle.js).
  // isCustomDomainHost is a shell function; elsewhere this is a platform page.
  metadata: (el) => {
    if (el.state?.root?.whitelabelBrand?.name) {
      return { title: el.call('pageTitle', 'auth.page.signup.title') }
    }
    const fns = el.context?.functions
    if (typeof fns?.isCustomDomainHost === 'function' && el.call('isCustomDomainHost')) {
      return { title: undefined }
    }
    return { title: el.call('pageTitle', 'auth.page.signup.title') }
  },
  flow: 'y',
  // Centred across, TOP-aligned down: the card centres itself down the page with
  // its own `margin: auto` (LoginWindow) while it fits, and a card taller than
  // the screen starts at the top and scrolls. `align: 'center center'` would
  // centre the OVERFLOW too, and the top of a tall card would sit above the
  // scroll origin, out of reach (853×533 = 150% zoom).
  align: 'center flex-start',
  // `C` on every side: with the privacy line, the sign-in line and the password
  // rule the card is 681px tall, and the old `D` above and below (68px each)
  // pushed a 768px laptop screen into a scroll.
  padding: 'C',
  width: '100%',
  // The page is AT LEAST the screen and grows with its card (it was exactly the
  // screen, `height: 100dvh`, which clamps a host that gives the page no scroller
  // of its own). In the shell the page is the `main` scroller itself, a fixed
  // screen high — what keeps the card whole there is `flexShrink` on the card
  // below.
  height: 'auto',
  minHeight: '100dvh',
  maxWidth: 'none',
  ...authPageTheme,

  // Initial state so `s.loading` is reactively read from the first paint —
  // CheckButton in SignupForm binds text/disabled/opacity to it.
  state: {
    loading: false,
    identifier: '',
    password: '',
    claimCredits: false,
    // SIGNUP-INTENT (symbols.app/built-with) — read ONCE in onInit, like
    // `claimCredits`, and forwarded on the register body. Flat strings: an
    // object value here would become a nested state instance.
    preset: '',
    acqSource: '',
    acqMedium: '',
    acqCampaign: '',
    acqRef: '',
    acqPlan: ''
  },

  onInit: (el, s) => {
    stashOAuthNext()
    // SIGNUP-INTENT — also kept for an OAuth sign-up, which never uses the
    // register body below (functions/signupIntent.js).
    el.call('stashSignupIntent')
    // CREDITS-CLAIM (tickets/sonnet.md) — read `?claim=credits` ONCE, at
    // construction, and carry it as state for the rest of the page's life.
    // The workspace router STRIPS the query string on the post-auth boot
    // redirect (separate ticket, opus.md), so reading `location.search`
    // again after that redirect would silently lose the intent. Reading it
    // here — before any navigation exists — and forwarding it as a body
    // field on `register()` (never as a query param on the redirect target)
    // is the fix the server side was built to expect.
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search)
      if (params.get('claim') === 'credits') s.quietUpdate({ claimCredits: true })
      // SIGNUP-INTENT — the symbols.app/built-with sign-up link carries
      // `preset` (an industry preset key), `plan` and where the visitor came
      // from (`ref` + `utm_*`). Read here for the same reason as the claim
      // above (the post-auth redirect strips the query). The server
      // shape-validates every value and drops a bad one — none of this can
      // block a signup (server core/utils/signupIntent.js).
      s.quietUpdate({
        preset: params.get('preset') || '',
        acqSource: params.get('utm_source') || '',
        acqMedium: params.get('utm_medium') || '',
        acqCampaign: params.get('utm_campaign') || '',
        acqRef: params.get('ref') || '',
        acqPlan: params.get('plan') || ''
      })
    }
  },

  LoginWindow: {
    // White-label host: the branded card shape (components/WhitelabelAuthWindow.js)
    // — inert on the platform.
    //
    // LoginWindow comes FIRST on purpose. The key order of a merged block is the
    // order in which the extends name each key, and the mixin names
    // `Flex.RequestAccessButtons`: listed first, it put the OAuth buttons ahead
    // of the form in the DOM (Tab went Google → GitHub → Email, sam F8), while
    // LoginWindow's own empty slots (`SignupForm: null` …, "keep keyboard order
    // aligned with the visible form-first layout") are what keep the form ahead
    // of them. The two declare no leaf in common — the mixin only adds
    // `.isBranded` blocks and brand-gated hides — so the order of the list moves
    // no value; pinned by __tests__/signupPageAudit.test.js.
    extends: ['LoginWindow', 'WhitelabelAuthWindow'],
    // A finished sign-up is a short card: it gives up the form's minimum height
    // and the section gap (LoginWindow pins `minHeight` so the content swap while
    // SIGNING IN does not resize it; this swap is the end of the page's job).
    isDone: (el, s) => !!s.done,
    '.isDone': { minHeight: 'auto', gap: 'A' },
    // The card is as tall as its content, never shorter. Two things made it
    // shorter on a short screen (853×533 = 150% zoom; persona elena, shots
    // 001–004: the white card ended above the Sign up button, the last rows and
    // the OAuth buttons hung below it on the grey):
    //   • the shell's `main` is a column flex scroller one screen high, and a
    //     flex item shrinks (`flex-shrink: 1`) down to its `minHeight` — so the
    //     card shrank to the screen while its content (`overflow: visible`)
    //     spilled out of it. `flexShrink: 0` keeps the card whole; `main` scrolls,
    //     and the card's own `margin: auto` centres it only while it fits, so the
    //     top of a tall card is never cut off;
    //   • ModalWindow caps a sheet at the screen minus the overlay gutter
    //     (`maxHeight: calc(100dvh - 2 * overlayGutter)`). This is a page, not an
    //     overlay: no cap.
    flexShrink: '0',
    maxHeight: 'none',
    // Helpers of this page. `scope`, not module-scope helpers: a module-local
    // function read from a handler is dropped when the component is frank'd
    // for platform delivery (the LoginWindow note above `completeGoogleLogin`),
    // and `scope` merges with LoginWindow's own (shallow, base first).
    scope: {
      // Where "Sign in" leads: the sign-in route carrying the live query/hash
      // (`?next=`, `claim`, campaign params — tickets/sonnet.md "auth-page
      // redirects still drop the query") MINUS `token`: the invitation is spent
      // at sign-up, and the invite page comes back through `next`.
      signInPath(el) {
        const live = new URL(
          el.call('withLiveQuery', '/login') || '/login',
          'https://local.invalid'
        )
        live.searchParams.delete('token')
        return live.pathname + live.search + live.hash
      },
      // The sentence under the heading of a finished sign-up. The address and
      // the credits go in through FUNCTION replacers (a string replacement
      // would expand `$&` inside them).
      doneMessage(el, s) {
        return el
          .call('polyglot', s.doneMessageKey)
          .replace('{email}', () => s.doneEmail)
          .replace('{credits}', () => String(s.doneCredits))
      },
      // Whether a failed register is the server refusing the PASSWORD — by the
      // body the SDK keeps as the error's cause (`error`, and `missing` once
      // the server lists the parts), else by the message it carries. Anything
      // else is the page's own error line (SignupFormError).
      isPasswordRefusal(ex) {
        const body = (ex && ex.cause && (ex.cause.cause || ex.cause)) || {}
        return (
          body.error === 'Password validation failed' ||
          (Array.isArray(body.missing) && body.missing.length > 0) ||
          /password (validation failed|must)/i.test(String((ex && ex.message) || ''))
        )
      },
      // The rule's parts for the checklist, from the policy the server
      // published (state: pwMin / pwLower / pwUpper / pwDigit / pwSpecial) and
      // the password as typed. Order = the server's: length, lower, upper,
      // digit, special.
      passwordParts(el, s) {
        const pw = String(s.password || '')
        const word = (key) => el.call('polyglot', key)
        const parts = []
        if (s.pwMin > 0) {
          parts.push({
            key: 'length',
            ok: pw.length >= s.pwMin,
            word: word('auth.signup.passwordRule.length').replace('{count}', () => String(s.pwMin))
          })
        }
        if (s.pwLower) {
          parts.push({
            key: 'lower',
            ok: /[a-z]/.test(pw),
            word: word('auth.signup.passwordRule.lower')
          })
        }
        if (s.pwUpper) {
          parts.push({
            key: 'upper',
            ok: /[A-Z]/.test(pw),
            word: word('auth.signup.passwordRule.upper')
          })
        }
        if (s.pwDigit) {
          parts.push({
            key: 'digit',
            ok: /[0-9]/.test(pw),
            word: word('auth.signup.passwordRule.digit')
          })
        }
        if (s.pwSpecial) {
          const chars = Array.from(String(s.pwSpecial))
          parts.push({
            key: 'special',
            ok: chars.some((c) => pw.includes(c)),
            word: word('auth.signup.passwordRule.special').replace('{chars}', () => chars.join(' '))
          })
        }
        return parts.map((p) => ({
          ...p,
          status: word(p.ok ? 'auth.signup.passwordRule.met' : 'auth.signup.passwordRule.unmet')
        }))
      },
      // Read the server's sign-up policy through the SDK, when the SDK can
      // (`getSignupPolicy` → GET /core/auth/signup-policy, public:
      // `{ password: { minLength, lower, upper, digit, special }, … }`). No
      // method, no answer, or a rule that is not a rule: the page keeps the
      // rule's own sentence. Flat state keys — an object would become a nested
      // state instance.
      async readPasswordPolicy(el, s) {
        const read = el.sdk && el.sdk.getSignupPolicy
        if (typeof read !== 'function') return
        try {
          const res = await read.call(el.sdk)
          const rule = (res && res.data && res.data.password) || (res && res.password) || null
          const min = rule ? Number(rule.minLength) : 0
          if (!rule || !Number.isInteger(min) || min < 1 || min > 128) return
          s.update({
            pwPolicy: true,
            pwMin: min,
            pwLower: rule.lower === true,
            pwUpper: rule.upper === true,
            pwDigit: rule.digit === true,
            pwSpecial: typeof rule.special === 'string' ? rule.special : ''
          })
        } catch (_) {
          /* the rule's own sentence stands */
        }
      },
      // The send-again cooldown: the seconds left tick down in state, once a
      // second, and stop when the card leaves the document. The timer lives on
      // the element (`el.__resendTimer`), never in state.
      startResendWait(el, s, seconds) {
        if (el.__resendTimer) clearInterval(el.__resendTimer)
        s.update({ resendWait: seconds })
        el.__resendTimer = setInterval(() => {
          const left = (s.resendWait || 0) - 1
          const gone = !el.node || el.node.isConnected === false
          if (left <= 0 || gone) {
            clearInterval(el.__resendTimer)
            el.__resendTimer = null
            if (!gone) s.update({ resendWait: 0 })
            return
          }
          s.update({ resendWait: left })
        }, 1000)
      },
      // "Continue" opens the new account: no second sign-in (R2-m10, persona
      // sam typed the address and the password again on /login). `register`
      // answers with the account's session tokens (the server mints them and
      // stores the refresh token). onSubmit keeps it on this card
      // (`__signupSession`: never in state, never in storage before the person
      // continues), and this opens it with the steps a sign-in takes (signin.js
      // onSubmit): the SDK adopts the tokens, getMe hydrates the session
      // (USER_UPDATED: the shell marks the person signed in before the router's
      // guard runs), initializeUserSession fills the root. Answers true once
      // the account is open. Answers false, with nothing half-open left
      // behind, when there is no session to open or opening it fails.
      async openSignupSession(el) {
        const card = typeof el.lookup === 'function' ? el.lookup('LoginWindow') : null
        if (!card) return false
        if (card.__signupOpened) return true
        const session = card.__signupSession
        card.__signupSession = null
        const sdk = el.sdk
        const tokens = session && session.tokens
        if (!tokens || !tokens.accessToken || !sdk) return false
        if (typeof sdk.adoptTokens !== 'function' || typeof sdk.getMe !== 'function') return false
        const exp = tokens.accessTokenExp || {}
        try {
          const adopted = await sdk.adoptTokens({
            accessToken: tokens.accessToken,
            refreshToken: tokens.refreshToken,
            expiresIn: exp.expiresIn,
            expiresAt: exp.expiresAt
          })
          if (!adopted) return false
          // A session on this origin: its refresh token is its own (review D1).
          el.call('markOwnSession')
          // No hydrated user, no open account: the shell would hold the
          // tokens and wait for a user that never comes.
          const me = await sdk.getMe()
          const user = me && (me.user || me)
          if (!user) throw new Error('no session user')
          await el.call('initializeUserSession', { loginData: { user, tokens } })
        } catch (_) {
          try {
            if (typeof sdk.logout === 'function') await sdk.logout()
          } catch (_e) {
            /* nothing more to undo */
          }
          return false
        }
        card.__signupOpened = true
        return true
      },
      // Where "Continue" goes once the account is open: where a sign-in from
      // signInPath would have sent the person (signin.js onSubmit). The
      // request's `next`, same-origin only (consumeOAuthNext's clamp); else the
      // checkout a paid-plan link queued; else home with the live query, minus
      // the spent invitation `token`. A stashed OAuth `next` is spent, not
      // followed: this sign-up made no OAuth round trip. A `?redirect=`
      // hand-off goes through sign-in, whose onInit hands the open session on;
      // a target that sign-in refuses goes home (sign-in would stop on its form).
      signedInNext(el) {
        const href = typeof window !== 'undefined' ? window.location.href : 'https://local.invalid/'
        const params = new URL(href).searchParams
        const redirect = params.get('redirect')
        if (redirect) {
          const target = el.call('classifyHandoffTarget', redirect)
          if (!target || target.verdict !== 'refused') return el.scope.signInPath(el)
        }
        el.call('consumeOAuthNext')
        const asked = params.get('next')
        const next = asked ? el.call('consumeOAuthNext', asked) : '/'
        if (next && next !== '/') return next
        const checkout = el.call('takeSignupCheckoutNext')
        if (checkout) return checkout
        const home = new URL(el.call('withLiveQuery', '/') || '/', 'https://local.invalid')
        home.searchParams.delete('token')
        home.searchParams.delete('next')
        return home.pathname + home.search + home.hash
      },
      // No account to open: sign-in, as before, with the address handed over
      // through the ROOT state (`_signInEmail`), never the URL. signin.js puts
      // it in the field, reads it once and clears it.
      handToSignIn(el, s) {
        if (s.root && s.doneEmail) s.root.update({ _signInEmail: s.doneEmail })
        return el.router(s.doneNext || '/login', el.getRoot())
      }
    },
    // The invitation's keys live HERE, on LoginWindow's own state — the scope
    // SignupForm and the header read (deep-merged with LoginWindow's own
    // declaration). Declared up front so every binding below tracks them
    // from the first paint.
    //   email / hasToken   the invited address, locked (emailed invitation)
    //   inviteEmailBound   false for a magic link: any address may join
    //   inviteTargetName / inviteInviterName   the preview's names
    //   pw*                the password rule the SDK read (pwPolicy false = none:
    //                      the rule's own sentence shows); passwordRefused: the
    //                      server turned the password down (cleared on typing)
    //   done*              the finished sign-up: what the card says, the address
    //                      it went to, whether the mail left, whether "Continue"
    //                      opens the new account (doneSignedIn) and where it goes
    //                      when it cannot (doneNext)
    //   resend*            the send-again control (busy / seconds left / its note)
    //   formError          '' | 'missing' | 'failed': the page's own error line under
    //                      the fields (formErrorText = the server's words for 'failed');
    //                      `name` is declared so the line's factory tracks it from the
    //                      first paint (an undefined read is not tracked)
    //   loading            LoginWindow's own key, declared again: this block
    //                      REPLACES the component's `state` (measured, smbls
    //                      3.14.841), and an undeclared `loading` read the ROOT
    //                      state's — the scrim, the "Creating…" button and the
    //                      Continue guard followed the shell's loading, not this card's
    state: {
      loading: false,
      email: '',
      name: '',
      hasToken: false,
      inviteEmailBound: true,
      inviteTargetName: '',
      inviteInviterName: '',
      pwPolicy: false,
      pwMin: 0,
      pwLower: false,
      pwUpper: false,
      pwDigit: false,
      pwSpecial: '',
      passwordRefused: false,
      formError: '',
      formErrorText: '',
      done: false,
      doneTitleKey: '',
      doneMessageKey: '',
      doneEmail: '',
      doneCredits: 0,
      doneMailed: false,
      doneNext: '',
      doneSignedIn: false,
      resendBusy: false,
      resendWait: 0,
      resendNoteKey: ''
    },
    onInit: (el, s) => {
      el.__inviteToken = _takeSignupInvite(el)
      _seedFromInviteToken(el, s, el.__inviteToken)
      _seedInvitePreview(el, s, el.__inviteToken)
      // The password rule, when the SDK offers the sign-up policy.
      if (el.scope && el.sdk && typeof el.sdk.getSignupPolicy === 'function') {
        el.scope.readPasswordPolicy(el, s)
      }
    },
    // The org's logo + name on a white-label host (the branded sign-in's
    // lockup, components/WhitelabelSignin.js); nothing on a platform host.
    WhitelabelLockup: {
      order: '-1',
      subscribeTo: ['whitelabelBrand'],
      hide: (el, s) => !(s.root && s.root.whitelabelBrand),
      // On this card the lockup sits on the design-system card ground, not
      // the branded sign-in's: its ink is the card's own (title / caption,
      // AA on that ground by the design system), and the name is not a
      // heading — the card's own heading stays the page's one h1.
      Wordmark: { tag: 'div', color: 'title' },
      Tagline: { color: 'caption' }
    },
    ModalHeader: {
      Hgroup: {
        H: {
          // Reachable by script, never by Tab: the finished sign-up puts focus
          // HERE (the form it was on has just left the page), so the next
          // thing a keyboard or a screen reader meets is the new heading.
          tabindex: '-1',
          width: 'fit-content',
          maxWidth: '100%',
          // Room for the focus ring: the brand ring (a box-shadow band) hugs the
          // element's own box, and a heading box is as tight as its glyphs. The
          // padding gives the band air; the equal negative margin takes the
          // same room back, so nothing around the heading moves.
          padding: 'X Y',
          margin: '-X -Y',
          round: 'radiusControl',
          // An invitation names what it joins; a white-label host names its
          // org (a brand without a name gets the neutral heading — never the
          // platform's "Symbols" on a branded host); the platform keeps its
          // own heading (Nika: "Keep Symbols on the platform"). Names go in
          // through a FUNCTION replacer: a string replacement would expand
          // `$&` / `$1` inside a name. A finished sign-up names what happened
          // instead ("Check your email").
          subscribeTo: ['whitelabelBrand'],
          text: (el, s) => {
            if (s.done) return el.call('polyglot', s.doneTitleKey)
            if (s.inviteTargetName) {
              const target = s.inviteTargetName
              return el
                .call('polyglot', 'auth.acceptInvite.h.welcomeNamed')
                .replace('{name}', () => target)
            }
            const brand = s.root?.whitelabelBrand
            if (!brand) return el.call('polyglot', 'auth.signup.heading')
            const org = brand.name
            return org
              ? el.call('polyglot', 'auth.signup.headingNamed').replace('{name}', () => org)
              : el.call('polyglot', 'auth.signup.headingNeutral')
          }
        },
        P: {
          hide: (el, s) => !s.inviteTargetName && !s.done,
          // The finished sign-up's sentence is THE message of the page, not a
          // subtitle: body size, body ink.
          fontSize: (el, s) => (s.done ? 'A' : 'Z1'),
          color: (el, s) => (s.done ? 'paragraph' : 'inactive'),
          text: (el, s) => {
            if (s.done) return el.scope.doneMessage(el, s)
            const named = !!s.inviteInviterName
            const key =
              s.inviteEmailBound === false
                ? named
                  ? 'auth.signup.invite.linkNamed'
                  : 'auth.signup.invite.link'
                : named
                  ? 'auth.signup.invite.boundNamed'
                  : 'auth.signup.invite.bound'
            return el.call('polyglot', key).replace('{name}', () => s.inviteInviterName || '')
          }
        }
      }
    },
    // WHITELABEL-SIGNUP-TYPE-1 (Nika decision A) — an OPTIONAL account-type
    // picker for the WHITE-LABEL branded signup. Feature-detected and inert
    // everywhere else: it renders ONLY when the branded org's public branding
    // record (resolved pre-auth on a custom-domain host — workspace
    // functions/whitelabelBrand.js) carries a `signupTypes` list. On
    // my.symbols.app and every platform host `root.whitelabelBrand` is null,
    // so this field stays hidden and contributes NO `type` — the Symbols
    // signup is byte-identical to before (same gated-child shape signin.js
    // uses for the branded WhitelabelSignin surface).
    //
    // ⛔ SECURITY BOUNDARY (decision A): the chosen value is a PROFILE
    // PREFERENCE only. Its ONLY effect is the `type` field on the register
    // body below. It triggers NO join/grant/switch, calls NO membership or
    // org endpoint, and auto-selects NO org. Access stays invite-driven. If
    // this is ever wired to anything other than the register `type`, stop —
    // that would cross decision A.
    AccountType: {
      extends: 'InputField',
      // Re-render when the brand lands AFTER first paint (the late-apply path
      // in whitelabelBrand.js), the same contract the branded signin uses.
      subscribeTo: ['whitelabelBrand'],
      width: '100%',
      // A finished sign-up has no field left to fill.
      hide: (el, s) => {
        if (s.done) return true
        const b = s.root && s.root.whitelabelBrand
        const list = b && Array.isArray(b.signupTypes) ? b.signupTypes : []
        return !list.some((t) => t && t.key && t.label)
      },
      display: (el, s) => {
        if (s.done) return 'none'
        const b = s.root && s.root.whitelabelBrand
        const list = b && Array.isArray(b.signupTypes) ? b.signupTypes : []
        return list.some((t) => t && t.key && t.label) ? null : 'none'
      },
      Title: {
        text: '{{ auth.signup.accountType | polyglot }}',
        padding: '- - 0'
      },
      // Reuses InputField's field styling verbatim (same tokens the Email /
      // Name / Password inputs use); only the tag and the option list differ.
      // The options come from the RECORD — never a hardcoded Searcher /
      // Investor list. The server's accountType.js is explicit: the canonical
      // list lives in the branded org's `branding`, not in client code.
      Input: {
        tag: 'select',
        name: 'accountType',
        theme: 'field',
        minHeight: 'controlH',
        round: 'radiusControl',
        fontSize: 'Z2',
        padding: '0 A',
        width: '100%',
        value: (el, s) => s.accountType || '',
        onChange: (ev, el, s) => s.update({ accountType: el.node.value }),
        childrenAs: 'props',
        // The children factory reads `s.root.whitelabelBrand` only behind a
        // truthiness guard, so the signal tracker never registers the dep on a
        // run where the brand is still null (the common first-paint case on a
        // slow lookup). Declare it explicitly so a late-arriving brand re-fires
        // the option list (FA515).
        childrenDeps: ['whitelabelBrand'],
        children: (el, s) => {
          const b = s.root && s.root.whitelabelBrand
          const list = b && Array.isArray(b.signupTypes) ? b.signupTypes : []
          const opts = list
            .filter((t) => t && t.key && t.label)
            .map((t) => ({ value: String(t.key), text: String(t.label) }))
          // Leading empty option: nothing is preselected, so a visitor who
          // ignores the field sends no `type` and the signup is unchanged.
          return [
            { value: '', text: el.call('polyglot', 'auth.signup.accountTypePlaceholder') }
          ].concat(opts)
        },
        childProps: {
          tag: 'option'
        }
      }
    },
    // The form and the OAuth column leave the card when the sign-up is done.
    Flex: {
      hide: (el, s) => !!s.done,
      SignupForm: {
        Password: {
          Input: {
            // The rule under the field describes it; a password the server
            // turned down is marked invalid until the next keystroke.
            aria: {
              describedby: 'signup-password-rule',
              invalid: (el, s) => (s.passwordRefused ? 'true' : null)
            }
          },
          // THE PASSWORD RULE ("Keep rule, show it" — Nika 2026-10-24). It sits
          // in the field's own label, so it is phrasing content (spans). Two
          // faces of ONE rule: the sentence (the first paint, and always when the
          // SDK cannot read the policy) and the live checklist (once the SDK has
          // read the server's policy — one swap, started in onInit, a few lines
          // of height, before a person is at the field). The refusal line below
          // is mounted from the first paint, empty, so the alert region exists
          // before it speaks (Rule 66).
          SignupPasswordRule: {
            tag: 'span',
            id: 'signup-password-rule',
            flow: 'y',
            gap: 'Y',
            fontSize: 'Z1',
            lineHeight: '1.4',
            color: 'caption',
            SignupPasswordRuleText: {
              tag: 'span',
              hide: (el, s) => !!s.pwPolicy,
              text: '{{ auth.signup.passwordRule.text | polyglot }}'
            },
            SignupPasswordRuleList: {
              tag: 'span',
              role: 'list',
              hide: (el, s) => !s.pwPolicy,
              flow: 'x',
              flexWrap: 'wrap',
              gap: 'Y A',
              children: (el, s) => (s.pwPolicy ? el.scope.passwordParts(el, s) : []),
              childrenAs: 'state',
              childProps: {
                tag: 'span',
                role: 'listitem',
                flow: 'x',
                gap: 'Y',
                align: 'center flex-start',
                isMet: (el, s) => !!s.ok,
                '.isMet': { color: 'successInk' },
                // The circle is the part's mark; the tick shows once it is met.
                // The words "met" / "not met" are for a screen reader only — the
                // state must not live in colour alone.
                SignupRuleMark: {
                  tag: 'span',
                  boxSize: 'iconSm',
                  flexShrink: '0',
                  round: 'radiusPill',
                  borderWidth: 'hairline',
                  borderStyle: 'solid',
                  borderColor: 'currentColor',
                  align: 'center center',
                  Icon: { name: 'checkmark', boxSize: 'iconXs', hide: (el, s) => !s.ok }
                },
                SignupRuleWord: { tag: 'span', text: (el, s) => s.word },
                SignupRuleStatus: { tag: 'span', ...SR_ONLY, text: (el, s) => `(${s.status})` }
              }
            },
            // Mounted, empty and without a box until the server refuses the
            // password: a live region must exist before its text does.
            SignupPasswordRefusal: {
              tag: 'span',
              role: 'alert',
              color: 'dangerInk',
              marginTop: (el, s) => (s.passwordRefused ? 'Y' : '0'),
              text: (el, s) =>
                s.passwordRefused ? el.call('polyglot', 'auth.signup.passwordRule.failed') : ''
            }
          }
        },
        // Under the button, in the footer (a div: the old `label` footer made a
        // click on any text beside the button press it). The page is for new
        // accounts, so the way back for an existing one sits right here, and the
        // two legal pages are two short links — never a sentence of consent.
        ModalFooter: {
          // THE PAGE'S OWN ERROR LINE — an empty submit, or a failed register. It
          // was a toast at the bottom centre of the screen: at 150% zoom (853×533)
          // it sat on the Sign up button (measured on the live dev shell: the
          // toast covered the button's centre). Here it is in the form, above the
          // button, mounted from the first paint and empty (so the alert region
          // exists before it speaks, and an empty line has no box — Rule 66). A
          // "missing" line goes away by itself once the three fields hold text.
          SignupFormError: {
            tag: 'p',
            role: 'alert',
            order: '-1',
            width: '100%',
            margin: '0',
            fontSize: 'Z2',
            lineHeight: '1.4',
            color: 'dangerInk',
            text: (el, s) => {
              if (s.formError === 'failed') {
                const title = el.call('polyglot', 'auth.signup.failed')
                return s.formErrorText ? `${title}: ${s.formErrorText}` : title
              }
              if (s.formError === 'missing' && !(s.email && s.name && s.password)) {
                return `${el.call('polyglot', 'auth.signup.missingFields.title')}: ${el.call('polyglot', 'auth.signup.missingFields.message')}`
              }
              return ''
            }
          },
          SignupHaveAccount: {
            flow: 'x',
            flexWrap: 'wrap',
            align: 'center center',
            gap: 'Y',
            margin: 'Z - -',
            fontSize: 'Z2',
            color: 'caption',
            SignupHaveAccountPrompt: {
              tag: 'span',
              text: '{{ auth.signup.haveAccount.prompt | polyglot }}'
            },
            SignupLink_signin: {
              fontWeight: '500',
              href: (el) => el.scope.signInPath(el),
              text: '{{ auth.signup.haveAccount.link | polyglot }}'
            }
          },
          // The privacy policy and the terms (persona elena: "I looked for a
          // privacy statement before I gave my name. I found none."). They open
          // in a new tab — the form must survive the read. The addresses are the
          // catalog's, one per language: the shell has no legal-link source of
          // its own; these are the public site's `/privacy` and `/terms` routes.
          // Not shown on a white-label host: those pages are Symbols'.
          SignupLegal: {
            subscribeTo: ['whitelabelBrand'],
            hide: (el, s) => !!(s.root && s.root.whitelabelBrand),
            flow: 'x',
            flexWrap: 'wrap',
            align: 'center center',
            gap: 'A',
            fontSize: 'Z1',
            // On a touch screen each link answers a 44pt square (SignupLink): the
            // two rows sit far enough apart that those squares do not overlap.
            '@coarse': { margin: 'A - -' },
            SignupLink_privacy: {
              target: '_blank',
              rel: 'noopener noreferrer',
              href: (el) => el.call('polyglot', 'auth.signup.legal.privacyHref'),
              text: '{{ auth.signup.legal.privacy | polyglot }}',
              aria: {
                label: (el) =>
                  `${el.call('polyglot', 'auth.signup.legal.privacy')} (${el.call('polyglot', 'auth.signup.legal.newTab')})`
              }
            },
            SignupLink_terms: {
              target: '_blank',
              rel: 'noopener noreferrer',
              href: (el) => el.call('polyglot', 'auth.signup.legal.termsHref'),
              text: '{{ auth.signup.legal.terms | polyglot }}',
              aria: {
                label: (el) =>
                  `${el.call('polyglot', 'auth.signup.legal.terms')} (${el.call('polyglot', 'auth.signup.legal.newTab')})`
              }
            }
          }
        }
      },
      // This is the SIGN-UP page: the OAuth column says so (the sign-in copy
      // read "Another way to sign in" / "Sign in with Google" above a form that
      // creates an account). "Continue with" is true for both a new and a
      // returning person — the server upserts.
      RequestAccessButtons: {
        Hgroup: {
          H: { text: '{{ auth.signup.alternativeHeading | polyglot }}' }
        },
        Flex: {
          GoogleSignInButton: { text: '{{ auth.signup.google | polyglot }}' },
          GithubSignInButton: { text: '{{ auth.signup.github | polyglot }}' }
        }
      }
    },
    // The announcement of a finished sign-up: mounted from the first paint and
    // empty (so the screen reader is already listening), read out when the
    // sentence arrives. The heading takes focus separately, so the two read as
    // "Check your email, heading" and then the sentence — not the same words twice.
    SignupAnnounce: {
      tag: 'p',
      role: 'status',
      ...SR_ONLY,
      text: (el, s) => {
        if (!s.done) return ''
        if (s.resendNoteKey) {
          return el.call('polyglot', s.resendNoteKey).replace('{email}', () => s.doneEmail)
        }
        const hint = s.doneMailed ? ` ${el.call('polyglot', 'auth.signup.checkEmail.hint')}` : ''
        return el.scope.doneMessage(el, s) + hint
      }
    },
    // THE FINISHED SIGN-UP. The card keeps its place; the form and the OAuth
    // column are gone; the heading and the sentence above say what happened.
    SignupDone: {
      hide: (el, s) => !s.done,
      flow: 'y',
      gap: 'A',
      width: '100%',
      // The address the mail went to, for the kinds whose own sentence does not
      // name it (credits, an invitation that still needs verifying). "Check
      // your email" itself names it in its sentence.
      SignupDoneTo: {
        tag: 'p',
        margin: '0',
        fontSize: 'Z2',
        color: 'paragraph',
        hide: (el, s) => !s.doneMailed || s.doneMessageKey === 'auth.signup.checkEmail.sent',
        text: (el, s) =>
          el.call('polyglot', 'auth.signup.checkEmail.sent').replace('{email}', () => s.doneEmail)
      },
      SignupDoneHint: {
        tag: 'p',
        margin: '0',
        fontSize: 'Z1',
        color: 'caption',
        hide: (el, s) => !s.doneMailed,
        text: '{{ auth.signup.checkEmail.hint | polyglot }}'
      },
      // The one way on: into the new account, signed in (scope.openSignupSession),
      // or, with no session to open, to sign-in with the address handed over
      // (scope.handToSignIn). While the account opens, LoginWindow's loading
      // scrim covers the card, as it does for a sign-in.
      CheckButton_continue: {
        // The tenant accent on a white-label host (inert on the platform). The
        // mixin comes AFTER CheckButton: its key names CheckButton already (the
        // part before `_`), and frank-audit FA806 reports the other order
        // ("rename to WhitelabelAccent: {} and drop the extends") on a key like this.
        // The mixin declares only `color` / `background` factories (undefined off
        // a branded host) and an `.isBranded` block, so CheckButton's theme paints
        // the platform button either way.
        extends: ['CheckButton', 'WhitelabelAccent'],
        type: 'button',
        icon: null,
        width: '100%',
        text: (el, s) =>
          el.call(
            'polyglot',
            s.doneSignedIn ? 'common.action.continue' : 'auth.signup.done.continue'
          ),
        onClick: async (ev, el, s) => {
          if (ev && ev.preventDefault) ev.preventDefault()
          if (s.loading) return
          if (!s.doneSignedIn) return el.scope.handToSignIn(el, s)
          s.update({ loading: true })
          if (!(await el.scope.openSignupSession(el))) {
            s.update({ loading: false })
            return el.scope.handToSignIn(el, s)
          }
          try {
            await el.router(el.scope.signedInNext(el), el.getRoot())
          } catch (_) {
            // The account is open and the route did not take: pressing again
            // routes again (openSignupSession answers true at once).
            s.update({ loading: false })
          }
        }
      },
      // SEND AGAIN — through the SDK, with a cooldown, and only where the SDK has
      // a way to do it from a signed-out page. Today it has none:
      // `resendVerification` needs the session this page does not have, and the
      // server answers it 401. The control stays out of the card until the SDK
      // offers `resendRegistrationConfirmation({ email })` (a public route that
      // answers alike for any address, rate-limited server-side); the cooldown
      // below keeps one person from asking again and again.
      Button_resend: {
        theme: 'quaternary',
        type: 'button',
        width: '100%',
        hide: (el, s) =>
          !s.doneMailed || !(el.sdk && typeof el.sdk.resendRegistrationConfirmation === 'function'),
        disabled: (el, s) => !!s.resendBusy || s.resendWait > 0,
        text: (el, s) =>
          s.resendWait > 0
            ? el
                .call('polyglot', 'auth.signup.resend.wait')
                .replace('{seconds}', () => String(s.resendWait))
            : el.call('polyglot', 'auth.signup.resend.action'),
        onClick: async (ev, el, s) => {
          if (ev && ev.preventDefault) ev.preventDefault()
          const send = el.sdk && el.sdk.resendRegistrationConfirmation
          if (typeof send !== 'function' || s.resendBusy || s.resendWait > 0) return
          s.update({ resendBusy: true, resendNoteKey: '' })
          let sent = false
          try {
            await send.call(el.sdk, { email: s.doneEmail })
            sent = true
          } catch (_) {
            /* the note below says so */
          }
          s.update({
            resendBusy: false,
            resendNoteKey: sent ? 'auth.signup.resend.sent' : 'auth.signup.resend.failed'
          })
          el.scope.startResendWait(el, s, sent ? 60 : 15)
        }
      },
      SignupResendNote: {
        tag: 'p',
        margin: '0',
        fontSize: 'Z1',
        color: 'caption',
        hide: (el, s) => !s.resendNoteKey,
        text: (el, s) =>
          s.resendNoteKey
            ? el.call('polyglot', s.resendNoteKey).replace('{email}', () => s.doneEmail)
            : ''
      }
    },
    onSubmit: async (ev, el, s) => {
      ev.preventDefault()
      const root = el.node
      const email = (s.email || root.querySelector('input[autocomplete="email"]')?.value || '')
        .trim()
        .toLowerCase()
      const password = s.password || root.querySelector('input[type="password"]')?.value || ''
      const name = (s.name || root.querySelector('input[autocomplete="name"]')?.value || '').trim()

      if (!email || !password || !name) {
        // Told in the form (SignupFormError), and the cursor goes to the first
        // field that is empty — a keyboard user lands where the work is.
        s.update({ formError: 'missing' })
        const first = !email ? 'Email' : !name ? 'Name' : 'Password'
        const form = el.Flex && el.Flex.SignupForm
        const input = form && form[first] && form[first].Input && form[first].Input.node
        if (input && typeof input.focus === 'function') input.focus()
        return
      }

      s.update({ loading: true, formError: '', formErrorText: '' })
      const inviteToken =
        el.__inviteToken ||
        (typeof window !== 'undefined'
          ? new URL(window.location.href).searchParams.get('token')
          : null)

      // SIGNUP-INTENT — only the fields the link actually carried.
      const intent = {
        source: s.acqSource,
        medium: s.acqMedium,
        campaign: s.acqCampaign,
        ref: s.acqRef,
        plan: s.acqPlan
      }
      const acquisition = {}
      Object.keys(intent).forEach((k) => {
        if (intent[k]) acquisition[k] = intent[k]
      })

      let registerResult
      try {
        const pluginSession = el.call('getPluginSessionFromUrl')
        const payload = {
          email,
          password,
          name,
          callbackUrl: `${window.location.origin}/signup-confirmation`,
          inviteToken,
          // WHITELABEL-SIGNUP-TYPE-1 (decision A) — the account type chosen on
          // the branded signup, persisted server-side as a PROFILE PREFERENCE
          // (user.preferences.accountType) that grants NO access. Feature-
          // detected: `undefined` on every platform host (no branded picker)
          // and whenever the visitor left the picker unset, so JSON.stringify
          // drops the key and the register request is byte-identical to
          // before. The server shape-gates it (accountType.js) and ignores a
          // malformed value — it can never block a signup.
          type: s.accountType || undefined,
          // CREDITS-CLAIM — the body flag, not the query param (see onInit
          // comment above). Claims inline, in this same request, and the
          // server hands the outcome back as `data.creditsClaim`.
          claimCredits: !!s.claimCredits,
          // SIGNUP-INTENT — `undefined` when the link carried none, so
          // JSON.stringify drops the keys and a plain signup is unchanged.
          // With a valid preset the server installs that industry's apps in
          // the new personal workspace (`data.presetApplied`).
          preset: s.preset || undefined,
          acquisition: Object.keys(acquisition).length ? acquisition : undefined
        }
        if (pluginSession) {
          registerResult = await el.sdk.register(payload, { session: pluginSession })
        } else {
          registerResult = await el.sdk.register(payload)
        }
      } catch (ex) {
        s.update({ loading: false })
        // The server turned the PASSWORD down: say so at the field (the rule is
        // printed right there), mark it, and put the cursor back in it. The
        // server's own text is an English list; the field's line is the page's.
        if (el.scope && el.scope.isPasswordRefusal(ex)) {
          s.update({ passwordRefused: true })
          const field = el.Flex && el.Flex.SignupForm && el.Flex.SignupForm.Password
          const input = field && field.Input && field.Input.node
          if (input && typeof input.focus === 'function') input.focus()
          return
        }
        s.update({ formError: 'failed', formErrorText: ex?.message || String(ex) })
        return
      }

      s.update({ loading: false })
      if (typeof window !== 'undefined' && window.plausible) window.plausible('Signup')

      // CREDITS-CLAIM acceptance: "ends with 100 in the top-up bucket AND is
      // told so on screen" — a human has to actually see this, once, on
      // arrival, without hunting for a balance. `creditsClaim` is only
      // present when `claimCredits` was requested; `null` means the claim
      // attempt failed server-side (registration still succeeded) — never
      // claim a landing that didn't happen. The card says it and STAYS until
      // the person moves on (it was a 6 s toast on the next page).
      const claim = registerResult?.creditsClaim
      const verified = !!registerResult?.user?.emailVerified
      // `verificationEmailSent: false` is the server saying the mail did not
      // leave (the transport failed); an older server sends no field, which
      // reads as "sent". An invitation that proved the address sends none.
      const mailFailed = registerResult?.verificationEmailSent === false
      let titleKey = 'auth.signup.checkEmail.title'
      let messageKey = 'auth.signup.checkEmail.sent'
      if (claim?.claimed) {
        titleKey = 'auth.signup.creditsAdded.title'
        messageKey = 'auth.signup.creditsAdded.message'
      } else if (claim?.alreadyClaimed) {
        // Symmetric with the standalone claim endpoint — impossible on a
        // genuinely fresh signup, but never render this as an error.
        titleKey = 'auth.signup.alreadyCredits.title'
        messageKey = 'auth.signup.alreadyCredits.message'
      } else if (inviteToken) {
        // An emailed invitation proved the address: the server accepted it
        // inline (verified). A magic link did not (INVITE-MAGIC-LINK-1): its
        // verification email is on its way, and the link joins only once the
        // address is verified (review D1) — so the card says to verify first.
        titleKey = 'auth.signup.inviteReady.title'
        messageKey = verified ? 'auth.signup.inviteReady.message' : 'auth.signup.inviteReady.verify'
      }
      if (mailFailed) {
        // Never "check your email" for a mail that was not sent.
        messageKey = 'auth.signup.notSent.message'
        if (titleKey === 'auth.signup.checkEmail.title') titleKey = 'auth.signup.notSent.title'
      }
      // The new account's session: `register` answers with its tokens. Kept
      // on this element for "Continue" (scope.openSignupSession), never in
      // state. The card offers to continue signed in only where the
      // SDK can adopt it; then the sentences that said "sign in" say what is
      // true.
      const tokens = registerResult && registerResult.tokens
      el.__signupSession = tokens && tokens.accessToken ? { tokens } : null
      el.__signupOpened = false
      const canOpen = !!el.__signupSession && !!el.sdk && typeof el.sdk.adoptTokens === 'function'
      if (canOpen) {
        const signedInKey = {
          'auth.signup.inviteReady.message': 'auth.signup.inviteReady.messageSignedIn',
          'auth.signup.inviteReady.verify': 'auth.signup.inviteReady.verifySignedIn',
          'auth.signup.notSent.message': 'auth.signup.notSent.messageSignedIn'
        }[messageKey]
        if (signedInKey) messageKey = signedInKey
      }
      // Never to `/signup-confirmation` (pre-existing #5, fixed with
      // INVITE-MAGIC-LINK-1). That page is the target of the EMAILED verify
      // link and runs its confirmation at once (it needs that link's `?token=`
      // + `&email=`): a sign-up routed there confirmed against whatever
      // `?token=` the sign-up URL carried — after an invited sign-up the
      // INVITATION token — and answered "Invalid confirmation link" (a plain
      // sign-up, with no token, the same). "Continue" opens the new account
      // and goes on signed in; `doneNext` is where it goes when there is no
      // session to open: sign-in (signInPath carries the live query/hash MINUS
      // `token`).
      s.update({
        done: true,
        doneTitleKey: titleKey,
        doneMessageKey: messageKey,
        doneEmail: email,
        doneCredits: Number(claim?.total ?? 100),
        doneMailed: !mailFailed && !verified,
        doneNext: el.scope ? el.scope.signInPath(el) : '/login',
        doneSignedIn: canOpen
      })
      // The form the person was on has just left the page: put focus on the
      // new heading (it was left on <body>, and a keyboard user started over).
      const heading = el.ModalHeader && el.ModalHeader.Hgroup && el.ModalHeader.Hgroup.H
      if (heading && heading.node && typeof heading.node.focus === 'function') {
        heading.node.focus()
      }
    }
  }
}
